/**
 * Primary navigation sidebar. Fixed on large screens; an off-canvas drawer on
 * mobile controlled by `open`/`onClose` (managed by AppLayout).
 */
import type { ReactElement, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import Logo from '@/components/common/Logo';

export interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

/** Minimal inline icon wrapper (24x24 stroke icons). */
function icon(path: ReactNode): ReactNode {
  return (
    <svg className="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {path}
    </svg>
  );
}

const stroke = { stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' };

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', end: true, icon: icon(<path d="M3 12l9-8 9 8M5 10v10h14V10" {...stroke} />) },
  { to: '/facebook-accounts', label: 'Tài khoản BM', icon: icon(<path d="M15 3h3a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2h3m0 4h6" {...stroke} />) },
  { to: '/ad-accounts', label: 'Ad Accounts', icon: icon(<path d="M4 6h16M4 12h16M4 18h10" {...stroke} />) },
  { to: '/campaigns', label: 'Campaigns', icon: icon(<path d="M11 5l9-2v14l-9-2M11 5v10M11 5H6a3 3 0 000 6h5m-6 0v6" {...stroke} />) },
  { to: '/rules', label: 'Rules', icon: icon(<path d="M4 6h16M4 12h16M4 18h16M8 4v4m8 4v4M6 14v4" {...stroke} />) },
  { to: '/logs', label: 'Logs', icon: icon(<path d="M8 6h11M8 12h11M8 18h11M4 6h.01M4 12h.01M4 18h.01" {...stroke} />) },
  { to: '/settings', label: 'Settings', icon: icon(<path d="M12 15a3 3 0 100-6 3 3 0 000 6zM19 12a7 7 0 00-.1-1l2-1.6-2-3.4-2.4 1a7 7 0 00-1.7-1L14.5 3h-5l-.3 2.6a7 7 0 00-1.7 1l-2.4-1-2 3.4 2 1.6a7 7 0 000 2l-2 1.6 2 3.4 2.4-1a7 7 0 001.7 1l.3 2.4h5l.3-2.6a7 7 0 001.7-1l2.4 1 2-3.4-2-1.6a7 7 0 00.1-1z" {...stroke} />) },
];

const linkBase =
  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';

/** Sidebar navigation (responsive drawer). */
export default function Sidebar({ open = false, onClose }: SidebarProps): ReactElement {
  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-gray-900/50 lg:hidden"
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-gray-200 bg-white transition-transform duration-200 lg:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Điều hướng chính"
      >
        <div className="flex h-16 items-center gap-2 border-b border-gray-200 px-5">
          <Logo className="h-8 w-8" />
          <span className="text-base font-bold text-gray-900">Ads360</span>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `${linkBase} ${
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      </aside>
    </>
  );
}
